/**
 * AudioDeviceSelector
 *
 * UI for selecting microphone and speaker devices.
 */

import {
  Alert,
  Button,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconInfoCircle,
  IconMicrophone,
  IconVolume,
} from '@tabler/icons-react';

export interface AudioDeviceSelectorProps {
  microphones: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
  selectedMicrophone: string;
  selectedSpeaker: string;
  currentSpeakerLabel: string;
  supportsSetSinkId: boolean;
  devicesLoaded: boolean;
  showMacBluetoothTip: boolean;
  disabled: boolean;
  /** The mic check's help panel owns the microphone picker (LUM-82). */
  hideMicrophone?: boolean;
  onSelectMicrophone: (deviceId: string) => void;
  onSelectSpeaker: (deviceId: string) => void;
  onTestSpeaker: () => void;
  onDismissMacBluetoothTip: () => void;
}

export function AudioDeviceSelector({
  microphones,
  speakers,
  selectedMicrophone,
  selectedSpeaker,
  currentSpeakerLabel,
  supportsSetSinkId,
  devicesLoaded,
  showMacBluetoothTip,
  disabled,
  hideMicrophone = false,
  onSelectMicrophone,
  onSelectSpeaker,
  onTestSpeaker,
  onDismissMacBluetoothTip,
}: Readonly<AudioDeviceSelectorProps>) {
  if (!devicesLoaded) {
    return (
      <Alert color="blue" variant="light">
        <Text size="sm">
          Requesting microphone access to show available devices...
        </Text>
      </Alert>
    );
  }

  return (
    <Stack gap="xs">
      {microphones.length > 1 && !hideMicrophone && (
        <Select
          label="Microphone"
          placeholder="Select microphone"
          leftSection={<IconMicrophone size={16} />}
          data={microphones.map(d => ({
            value: d.deviceId,
            label: d.label || `Microphone ${d.deviceId.slice(0, 8)}`,
          }))}
          value={selectedMicrophone}
          onChange={value => value && onSelectMicrophone(value)}
          disabled={disabled}
          styles={{
            input: {
              backgroundColor: 'var(--mantine-color-white)',
            },
          }}
        />
      )}

      {supportsSetSinkId && speakers.length > 0 ? (
        <Group align="end" wrap="nowrap" gap="sm">
          <Select
            flex={1}
            label="Speaker"
            placeholder="Select speaker"
            leftSection={<IconVolume size={16} />}
            data={speakers.map(d => ({
              value: d.deviceId,
              label: d.label || `Speaker ${d.deviceId.slice(0, 8)}`,
            }))}
            value={selectedSpeaker}
            onChange={value => value && onSelectSpeaker(value)}
            disabled={disabled}
            styles={{
              input: {
                backgroundColor: 'var(--mantine-color-white)',
              },
            }}
          />
          <Button
            variant="light"
            onClick={onTestSpeaker}
            disabled={disabled}
            size="sm"
          >
            Test
          </Button>
        </Group>
      ) : (
        <TextInput
          label="Speaker"
          value={currentSpeakerLabel}
          leftSection={<IconVolume size={16} />}
          readOnly
          disabled
          description="Audio will play through your system's default speaker"
          styles={{
            input: {
              backgroundColor: 'var(--mantine-color-gray-1)',
              cursor: 'not-allowed',
            },
          }}
        />
      )}

      {showMacBluetoothTip && (
        <Alert
          color="gray"
          variant="light"
          icon={<IconInfoCircle size={16} />}
          withCloseButton
          onClose={onDismissMacBluetoothTip}
        >
          <Text size="sm">
            Tip for macOS Bluetooth: for best audio, use your Mac&apos;s
            built-in mic and your headset for output.
          </Text>
        </Alert>
      )}

      {microphones.length === 0 && (
        <Alert
          color="yellow"
          variant="light"
          icon={<IconAlertCircle size={16} />}
        >
          <Text size="sm">
            No microphone detected. Please check your device connections and
            browser permissions.
          </Text>
        </Alert>
      )}
    </Stack>
  );
}
